import { useState, useCallback, useRef, useEffect } from "react";
import moment from "moment";
import { CalendarEvent, ClientTimeSlot, isGroupPatientType, isGroupTherapistType } from "../controller/adapters";
import notificationStore from "../../../stores/Notification/notificationStore";
import { eventToClientTimeSlot } from "../controller/adapters/display-to-client/reverse.adapter";
import { useCancelContinuous } from "../../../stores/Appointment/appointment.mutation";
import {
  useUpdateTimeSlot,
  useUpdateContinuousTimeSlot,
} from "../../../stores/Calendar/calendar.mutations";
import { useCachedAppointmentLookup } from "../../../stores/Calendar/calendar.queries";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { useGuardedWorkHourExceptionMutations } from "../lunchBreak/useGuardedWorkHourExceptionMutations";
import {
  WorkHourExceptionDtoTypeEnum,
  WorkHourExceptionDtoInitiatorEnum,
} from "../../../api-client";

export interface MousePosition {
  mouseX: number | null;
  mouseY: number | null;
}

export interface ContextMenuData {
  event: CalendarEvent | null;
  timeSlot: ClientTimeSlot | null;
  mousePosition: MousePosition;
}

// Core handlers needed from Calendar component
export interface ContextMenuHandlers {
  onRemoveEvent: (timeslotId: string, event?: CalendarEvent) => void;
  onRemoveWorkingHours?: (exceptionId: string) => void;
  onRemoveContinuousEvent: (timeslotId: string) => void;
  onRemoveGroupTherapist: (timeslotId: string) => void;
  onMoveEventDate: (event: any) => void;
  onAddShelfEvent: (event: any) => void;
  onOutdated: () => void;
  onCloseNotesDialog: () => void;
  // allow controller to set patient id in global store
  setSelectedPatientId: (id: number) => void;
  // optional: consumer can react when wait list opens with a specific event
  onWaitListOpen?: (event: CalendarEvent) => void;
  // Cancel entire day handler
  onCancelDayClick?: (userId: string, date: string, userName: string) => void;

  // Calendar refs and state
  calendarRef: React.RefObject<any>;
  calendarEvents: CalendarEvent[];
  isContinuous: boolean;
}

export const useContextMenuController = (handlers: ContextMenuHandlers) => {
  // Mutation hooks
  const { mutateAsync: cancelContinuousAppointment } = useCancelContinuous();
  const { mutate: updateEventTimeSlot } = useUpdateTimeSlot();
  const { mutate: updateContinuousEventTimeSlot } = useUpdateContinuousTimeSlot();

  // Use cached appointment lookup (searches all cached appointments)
  const { lookupByAppointmentId } = useCachedAppointmentLookup();

  // Break split (MD-238): writes go through the guarded work-hour-exception
  // mutations — the backend composes the day, enforces §4 and validates
  // positions. No keep/delete prompt on this path.
  const { createWorkHourException, updateWorkHourException } = useGuardedWorkHourExceptionMutations({
    promptOnOptionalBreak: false,
  });

  // Calendar store for dialog state management
  const CalendarStore = useStore(CalendarContext);

  const [isOpen, setIsOpen] = useState(false);
  const [contextMenuEvent, setContextMenuEvent] = useState<CalendarEvent | null>(null);
  const [clickedTimeSlot, setClickedTimeSlot] = useState<ClientTimeSlot | null>(null);
  const [mousePosition, setMousePosition] = useState<MousePosition>({
    mouseX: null,
    mouseY: null,
  });

  // Dialog states
  const [isCancellationDialogOpen, setIsCancellationDialogOpen] = useState(false);
  const [isContinuousCancelationWarningOpen, setContinuousCancelationWarningOpen] = useState(false);
  const [isAppointmentEditOpen, setIsAppointmentEditOpen] = useState(false);
  const [overviewAppointmentPatientId, setOverviewAppointmentPatientId] = useState<number | null>(
    null
  );

  const [overviewAppointmentRxId, setOverviewAppointmentRxId] = useState<string | undefined>( undefined);
  const [waitListOpen, setWaitListOpen] = useState(false);
  const [waitListEvent, setWaitListEvent] = useState<CalendarEvent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [isEditCancellationDialogOpen, setEditCancellationDialogOpen] = useState(false);
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [deletionMessage, setDeletionMessage] = useState<string | null>(null);
  const [deletionConfirmCallback, setDeletionConfirmCallback] = useState<(() => void) | null>(null);
  const [editTitleDialogOpen, setEditTitleDialogOpen] = useState(false);
  const [editTitleEvent, setEditTitleEvent] = useState<CalendarEvent | null>(null);

  // Selected data refs
  const selectedAppointmentRef = useRef<ClientTimeSlot | null>(null);

  const openContextMenu = useCallback((event: CalendarEvent, clientX: number, clientY: number) => {
    // console.debug("[ContextMenuController] openContextMenu invoked", { event, clientX, clientY });
    setContextMenuEvent(event);

    // console.debug("[ContextMenuController] openContextMenu extended", event.extendedProps);
    if (event.extendedProps?.eventType) {
      setClickedTimeSlot(eventToClientTimeSlot(event));
    }
    setMousePosition({
      mouseX: clientX - 2,
      mouseY: clientY - 4,
    });
    setIsOpen(true);
  }, []);

  const closeContextMenu = useCallback(() => {
    setIsOpen(false);
    setContextMenuEvent(null);
    setClickedTimeSlot(null);
    setMousePosition({ mouseX: null, mouseY: null });
  }, []);

  // Debug state transitions
  useEffect(() => {
    // console.debug("[ContextMenuController] state change", {
      // isOpen,
      // mousePosition,
      // contextMenuEvent,
      // clickedTimeSlot,
    // });
  }, [isOpen, mousePosition, contextMenuEvent, clickedTimeSlot]);

  // Handler implementations
  const handleRemoveGoneFishing = useCallback(
    (event: CalendarEvent) => {
      closeContextMenu();
      setDeletionMessage(
        `Sind Sie sicher, dass Sie den Eintrag '${event.title ?? ""}' löschen möchten?`
      );
      setDeletionConfirmCallback(() => () => {
        handlers.calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
        const timeslotId = event.id!;
        closeContextMenu();
        return handlers.onRemoveEvent && handlers.onRemoveEvent(timeslotId, event);
      });
      setDeletionDialogOpen(true);
    },
    [handlers, closeContextMenu]
  );

  const handleRemoveEventTimeSlot = useCallback(
    (event: CalendarEvent) => {
      closeContextMenu();
      
      // Special message for lunch breaks
      const isLunchBreak = event.extendedProps?.timeSlotType === 'LunchBreak';
      const message = isLunchBreak
        ? `Möchten Sie die Pause für diesen Tag entfernen? Dies betrifft nur den ${moment(event.start).format("DD.MM.YYYY")}.`
        : `Sind Sie sicher, dass Sie den Eintrag '${event.title ?? ""}' löschen möchten?`;
      
      setDeletionMessage(message);
      setDeletionConfirmCallback(() => () => {
        // Lunch breaks are NOT removed optimistically: the deletion can be
        // legally blocked (§ 4 ArbZG) and imperatively removed events don't
        // come back on a refetch of identical data. The query invalidation
        // removes the event on success and keeps it on failure.
        if (!isLunchBreak) {
          handlers.calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
        }
        const timeslotId = event.id!;
        closeContextMenu();
        return handlers.onRemoveEvent && handlers.onRemoveEvent(timeslotId, event);
      });
      setDeletionDialogOpen(true);
    },
    [handlers, closeContextMenu]
  );

  /**
   * Split a lunch break into two halves (MD-238, §4 ArbZG allows blocks of
   * ≥15 min). The second half is placed 30 min after the first so the two
   * do not immediately re-merge (back-to-back breaks are auto-merged by the
   * backend — that IS the undo gesture). Only breaks of ≥30 min can be
   * split, so both halves stay ≥15 min and keep counting toward the
   * statutory minimum.
   */
  const handleSplitLunchBreak = useCallback(
    async (event: CalendarEvent) => {
      closeContextMenu();
      try {
        const userId = event.extendedProps?.user?.id || event.resourceId;
        if (!userId || !event.start || !event.end) {
          throw new Error("Cannot split break: missing user or time range");
        }
        const start = moment(event.start);
        const end = moment(event.end);
        const durationMinutes = end.diff(start, "minutes");
        if (durationMinutes < 30) {
          notificationStore.showMessage(
            "Diese Pause ist zu kurz zum Teilen — beide Teile müssen mindestens 15 Minuten lang sein (§ 4 ArbZG).",
            "warning"
          );
          return;
        }

        const halfMinutes = Math.floor(durationMinutes / 2);
        const gapMinutes = 30;
        const date = start.format("YYYY-MM-DD");

        const firstHalf = {
          slotId: event.extendedProps?.slotId, // keep the original break's identity
          isBreak: true,
          start: start.toISOString(),
          end: start.clone().add(halfMinutes, "minutes").toISOString(),
        };
        const secondHalf = {
          isBreak: true,
          start: start.clone().add(halfMinutes + gapMinutes, "minutes").toISOString(),
          end: start.clone().add(durationMinutes + gapMinutes, "minutes").toISOString(),
        };

        const exceptionId = event.extendedProps?.exceptionId;
        if (exceptionId) {
          await updateWorkHourException({
            exceptionId,
            exception: {
              userId,
              date,
              type: WorkHourExceptionDtoTypeEnum.Override,
              slots: [firstHalf, secondHalf],
              initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
            },
          });
        } else {
          await createWorkHourException({
            exception: {
              userId,
              date,
              type: WorkHourExceptionDtoTypeEnum.Override,
              slots: [firstHalf, secondHalf],
              initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
            },
          });
        }
      } catch (error) {
        console.error("Error splitting lunch break:", error);
        notificationStore.showMessage("Fehler beim Teilen der Pause", "error", error as any);
      }
    },
    [closeContextMenu, createWorkHourException, updateWorkHourException]
  );

  const handleRemoveLeaveOfAbsence = useCallback(
    (event: CalendarEvent) => {
      closeContextMenu();
      setDeletionMessage(
        `Sind Sie sicher, dass Sie den Eintrag '${event.title ?? ""}' löschen möchten?`
      );
      setDeletionConfirmCallback(() => () => {
        handlers.calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
        const timeslotId = event.id!;
        closeContextMenu();
        return handlers.onRemoveEvent && handlers.onRemoveEvent(timeslotId);
      });
      setDeletionDialogOpen(true);
    },
    [handlers, closeContextMenu]
  );

  const handleRemoveSimpleWorkingHours = useCallback(
    (event: CalendarEvent) => {
      closeContextMenu();
      const exceptionId = event.extendedProps?.exceptionId;
      
      // If working hours don't have exceptionId, check if there's a lunch break exception for the same day
      if (!exceptionId) {
        const userId = event.resourceId || event.extendedProps?.user?.id;
        const eventDate = event.start;
        
        // Find lunch break for the same user and date with an exceptionId
        const lunchBreakException = handlers.calendarEvents?.find(e => 
          e.extendedProps?.timeSlotType === 'LunchBreak' &&
          (e.resourceId === userId || e.extendedProps?.user?.id === userId) &&
          e.start && eventDate &&
          moment(e.start).isSame(eventDate, 'day') &&
          e.extendedProps?.exceptionId
        );
        
        if (!lunchBreakException) {
          // No exceptions at all for this day - cannot be deleted
          notificationStore.showMessage(
            "Standard-Arbeitszeiten können nicht gelöscht werden. Sie können sie nur ändern oder den ganzen Tag absagen.",
            "info"
          );
          return;
        }
        
        // Found lunch break exception - delete it
        const lunchExceptionId = lunchBreakException.extendedProps?.exceptionId;
        setDeletionMessage(
          `Sind Sie sicher, dass Sie die Ausnahme löschen möchten? Die ursprünglichen Arbeitszeiten werden wiederhergestellt.`
        );
        setDeletionConfirmCallback(() => () => {
          handlers.calendarRef.current?.getApi()?.getEventById(lunchBreakException.id!)?.remove();
          closeContextMenu();
          return (handlers as any).onRemoveWorkingHours && (handlers as any).onRemoveWorkingHours(lunchExceptionId);
        });
        setDeletionDialogOpen(true);
        return;
      }
      
      // Working hours have exceptionId - delete the exception
      setDeletionMessage(
        `Sind Sie sicher, dass Sie die Ausnahme '${event.title ?? ""}' löschen möchten? Die ursprünglichen Arbeitszeiten werden wiederhergestellt.`
      );
      setDeletionConfirmCallback(() => () => {
        handlers.calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
        closeContextMenu();
        return (handlers as any).onRemoveWorkingHours && (handlers as any).onRemoveWorkingHours(exceptionId);
      });
      setDeletionDialogOpen(true);
    },
    [handlers, closeContextMenu]
  );

  const handleRemoveContinuousEventTimeSlot = useCallback(
    (event: CalendarEvent) => {
      closeContextMenu();
      setDeletionMessage(
        `Sind Sie sicher, dass Sie den Eintrag '${event.title ?? ""}' löschen möchten?`
      );
      setDeletionConfirmCallback(() => () => {
        handlers.calendarRef.current?.getApi()?.getEventById(event.id!)?.remove();
        const timeslotId = event.id!;
        closeContextMenu();
        return handlers.onRemoveContinuousEvent && handlers.onRemoveContinuousEvent(timeslotId);
      });
      setDeletionDialogOpen(true);
    },
    [handlers, closeContextMenu]
  );
  const handleRemoveGroupTherapist = useCallback(
    (event: CalendarEvent) => {
      closeContextMenu();
      setDeletionMessage(
        `Sind Sie sicher, dass Sie den Eintrag '${event._def?.title ?? ""}' löschen möchten?`
      );
      setDeletionConfirmCallback(() => () => {
        handlers.calendarRef.current?.getApi()?.getEventById(event.extendedProps?.timeSlotId!)?.remove();
        const timeslotId = event.extendedProps?.timeSlotId!;
        closeContextMenu();
        return handlers.onRemoveGroupTherapist && handlers.onRemoveGroupTherapist(timeslotId);
      });
      setDeletionDialogOpen(true);
    },
    [handlers, closeContextMenu]
  );
  // Edit the title of an existing (Continuous)EventTimeSlot. Opens a small
  // dialog; on confirm the change is persisted via the existing update-timeslot
  // mutation. The backend overwrites start/end on update, so we resend the
  // event's current window unchanged alongside the new title.
  const handleEditEventTitle = useCallback(
    (event: CalendarEvent) => {
      closeContextMenu();
      setEditTitleEvent(event);
      setEditTitleDialogOpen(true);
    },
    [closeContextMenu]
  );

  const closeEditTitleDialog = useCallback(() => {
    setEditTitleDialogOpen(false);
    setEditTitleEvent(null);
  }, []);

  const confirmEditTitle = useCallback(
    (newTitle: string) => {
      const event = editTitleEvent;
      setEditTitleDialogOpen(false);
      setEditTitleEvent(null);
      if (!event?.id) return;

      const isContinuousEvent =
        event.extendedProps?.timeSlotType === "ContinuousEventTimeSlot" || handlers.isContinuous;

      const timeslot = {
        id: event.id,
        type: isContinuousEvent ? "ContinuousEventTimeSlot" : "EventTimeSlot",
        title: newTitle,
      };

      if (isContinuousEvent) {
        updateContinuousEventTimeSlot({ id: event.id, timeslot });
      } else {
        updateEventTimeSlot({ id: event.id, timeslot });
      }
    },
    [editTitleEvent, handlers.isContinuous, updateEventTimeSlot, updateContinuousEventTimeSlot]
  );

  const confirmDeletion = useCallback(() => {
    if (deletionConfirmCallback) {
      try {
        deletionConfirmCallback();
      } finally {
        setDeletionConfirmCallback(null);
        setDeletionMessage(null);
        setDeletionDialogOpen(false);
      }
    } else {
      setDeletionDialogOpen(false);
    }
  }, [deletionConfirmCallback]);

  const closeDeletionDialog = useCallback(() => {
    setDeletionDialogOpen(false);
    setDeletionMessage(null);
    setDeletionConfirmCallback(null);
  }, []);

  const handleMoveEvent = useCallback(
    (event: any) => {
      // console.log("Moving event from calendar", event);

      closeContextMenu();
      
      const isGroupPatient = isGroupPatientType(event.extendedProps?.timeSlotType);
      const isSingleSlotMove = !!event.extendedProps?.moveSingleSlot;
      
      handlers.calendarRef.current
        ?.getApi()
        ?.getEvents()
        .forEach((e: any) => {
          // "Nur diese" (single-slot move) lifts ONLY the clicked slot's event;
          // a whole-appointment move lifts every event sharing the groupId.
          // Events without a groupId (FullCalendar normalizes it to "") must
          // NEVER be matched by groupId — "" === "" would sweep every other
          // groupId-less event (all EventTimeSlots, lunch breaks, …) off the
          // calendar — so fall back to an exact id match.
          const shouldRemove = isSingleSlotMove
            ? e.id === event.id
            : event.groupId
              ? e.groupId === event.groupId && !isGroupPatient
              : e.id === event.id;
          if (shouldRemove) {
            // console.log("Removing related event:", e.id);
            e.remove();
          }
        });

      // Note: when a group patient is moved out, the therapist event remains on the calendar
      // even if no participants remain. Only explicit deletion (Löschen) should remove it.

      // console.log("Termin aus dem kalender entfernt");
      return handlers.onMoveEventDate && handlers.onMoveEventDate(event);
    },
    [handlers, closeContextMenu]
  );

  const handleAddShelfEvent = useCallback(
    (event: CalendarEvent) => {
      // console.log("Adding shelf event", event);
      closeContextMenu();
      return handlers.onAddShelfEvent && handlers.onAddShelfEvent(event as any);
    },
    [handlers, closeContextMenu]
  );

  const handleWaitList = useCallback(
    (event: CalendarEvent) => {
      // Store the event separately so closing the context menu doesn't lose it
      setWaitListEvent(event);
      // We intentionally do NOT rely on contextMenuEvent (it will be cleared in closeContextMenu)
      closeContextMenu();
      setWaitListOpen(true); // always open (no toggle race)
    },
    [closeContextMenu]
  );

  const handleCancelAppointment = useCallback(
    (timeSlot: ClientTimeSlot) => {
      selectedAppointmentRef.current = timeSlot;
      if (timeSlot?.patient?.id) {
        try {
          handlers.setSelectedPatientId(Number(timeSlot.patient.id));
        } catch {}
      }
      closeContextMenu();
      setIsCancellationDialogOpen(true);
    },
    [closeContextMenu]
  );

  const handleCancelContinuousAppointment = useCallback(
    (timeSlot: ClientTimeSlot) => {
      selectedAppointmentRef.current = timeSlot;
      if (timeSlot?.patient?.id) {
        try {
          handlers.setSelectedPatientId(Number(timeSlot.patient.id));
        } catch {}
        closeContextMenu();
        setContinuousCancelationWarningOpen(true);
      }
    },
    [closeContextMenu]
  );

  const handleAttended = useCallback(
    (timeSlot: ClientTimeSlot) => {
      selectedAppointmentRef.current = timeSlot;
      if (timeSlot?.patient?.id) {
        try {
          handlers.setSelectedPatientId(Number(timeSlot.patient.id));
        } catch {}
      }
      closeContextMenu();
      setDialogOpen(true);
    },
    [closeContextMenu]
  );

  const handleEditAppointment = useCallback(
    (timeSlot: ClientTimeSlot) => {
      // console.log("Editing appointment", timeSlot);
      selectedAppointmentRef.current = timeSlot;
      if (timeSlot?.patient?.id) {
        try {
          handlers.setSelectedPatientId(Number(timeSlot.patient.id));
        } catch {}
      }
      closeContextMenu();
      setIsAppointmentEditOpen(true);
    },
    [closeContextMenu]
  );

  const handleUpdateCancelledAppointment = useCallback(
    (timeSlot: ClientTimeSlot) => {
      // const appointment = handlers.appointments?.find((a) => a.timeSlots?.some((ts) => ts.id === timeSlot.id));
      // if (!appointment) {
      //   console.error("Appointment not found for timeslot", timeSlot);
      //   return;
      // }
      selectedAppointmentRef.current = timeSlot;
      if ((timeSlot as any)?.patient?.id) {
        try {
          handlers.setSelectedPatientId(Number((timeSlot as any).patient.id));
        } catch {}
      }
      closeContextMenu();
      setEditCancellationDialogOpen(true);
    },
    [closeContextMenu]
  );

  const handleEditContinuousAppointment = useCallback(
    (timeSlot: ClientTimeSlot) => {
      // console.log("Editing continuous appointment", timeSlot);
      selectedAppointmentRef.current = timeSlot;
      if (timeSlot?.patient?.id) {
        try {
          handlers.setSelectedPatientId(Number(timeSlot.patient.id));
        } catch {}

        closeContextMenu();
        setIsAppointmentEditOpen(true);
      }
    },
    [closeContextMenu]
  );

  const handleAppointmentOverview = useCallback(
    (patientId: number,rxId?: string) => {
      // console.debug("Opening appointment overview for patient", patientId,rxId);
      if (!patientId) {
        notificationStore.showMessage("No patient associated with this appointment.", "error");
        return;
      }
      setOverviewAppointmentPatientId(patientId);
      if (rxId) {
        setOverviewAppointmentRxId(rxId);
      }
      closeContextMenu();
    },
    [closeContextMenu]
  );

  const handleOpenNotesDialog = useCallback(
    (timeSlot: ClientTimeSlot) => {
      selectedAppointmentRef.current = timeSlot;
      if (timeSlot?.patient?.id) {
        try {
          handlers.setSelectedPatientId(Number(timeSlot.patient.id));
        } catch {}
      }
      closeContextMenu();
      if (timeSlot.appointmentId) {
        CalendarStore.openNotesDialog(timeSlot.appointmentId);
      }
    },
    [closeContextMenu, handlers, CalendarStore]
  );

  const handleCloseNotesDialog = useCallback(() => {
    closeContextMenu();
    handlers.onOutdated();
  }, [closeContextMenu, handlers]);

  // Dialog close handlers
  const handleCloseCancellation = useCallback((actionPerformed: boolean) => {
    setIsCancellationDialogOpen(false);
    // No onOutdated() refresh here: the cancel mutations themselves already
    // invalidate appointmentsForDate.all() + every related scope (see
    // useCancelAppointment / useCancelTimeSlot). The wholesale refresh on
    // dialog close was a redundant second invalidation of the same caches.
  }, []);

  const handleCloseEditAppointment = useCallback(
    (actionPerformed: boolean) => {
      setIsAppointmentEditOpen(false);
      if (actionPerformed) {
        handlers.onOutdated();
      }
    },
    [handlers]
  );

  const handleCloseEditCancelledAppointment = useCallback(
    (actionPerformed: boolean) => {
      setEditCancellationDialogOpen(false);
      if (actionPerformed) {
        handlers.onOutdated();
      }
    },
    [handlers]
  );

  const handleCloseOverviewAppointment = useCallback(() => {
    setOverviewAppointmentPatientId(null);
  }, []);

  const handleWaitListClose = useCallback(() => {
    setWaitListOpen(false);
    setWaitListEvent(null);
  }, []);

  const onCancelContinuousAppointmentConfirm = useCallback(
    async (doAction: boolean) => {
      setContinuousCancelationWarningOpen(false);

      if (!doAction) {
        return;
      }
      if (selectedAppointmentRef.current?.appointmentId) {
        try {
          await cancelContinuousAppointment({
            appointmentId: selectedAppointmentRef.current.appointmentId,
          });
          handlers.onOutdated(); // Refresh calendar data
        } catch (error) {
          console.error("Failed to cancel continuous appointment:", error);
          // Error notification is handled by the mutation
        }
      }
    },
    [selectedAppointmentRef.current, cancelContinuousAppointment, handlers]
  );

  const onCloseNotesDialogEvent = useCallback(() => {
    CalendarStore.closeNotesDialog();
    return handlers.onCloseNotesDialog && handlers.onCloseNotesDialog();
  }, [handlers, CalendarStore]);

  return {
    // State
    isOpen,
    contextMenuEvent,
    clickedTimeSlot,
    mousePosition,

    // Dialog states
    isCancellationDialogOpen,
    isContinuousCancelationWarningOpen,
    isAppointmentEditOpen,
    isEditCancellationDialogOpen,
    overviewAppointmentPatientId,
    overviewAppointmentRxId,
    waitListOpen,
    dialogOpen,
    selectedAppointmentRef,

    // Actions
    openContextMenu,
    closeContextMenu,

    // Handlers
    handleRemoveGoneFishing,
    handleRemoveEventTimeSlot,
    handleSplitLunchBreak,
    handleRemoveLeaveOfAbsence,
    handleRemoveSimpleWorkingHours,
    handleRemoveContinuousEventTimeSlot,
    handleEditEventTitle,
    handleRemoveGroupTherapist,
    handleMoveEvent,
    handleAddShelfEvent,
    handleWaitList,
    handleCancelAppointment,
    handleCancelContinuousAppointment,
    handleUpdateCancelledAppointment,
    handleAttended,
    handleEditAppointment: handlers.isContinuous
      ? handleEditContinuousAppointment
      : handleEditAppointment,
    handleAppointmentOverview,
    handleOpenNotesDialog,
    handleCloseNotesDialog,

    // Dialog handlers
    handleCloseCancellation,
    handleCloseEditAppointment,
    handleCloseOverviewAppointment,
    handleCloseEditCancelledAppointment,
    handleWaitListClose,
    onCancelContinuousAppointmentConfirm,

    onCloseNotesDialogEvent,
    setShowNotesDialog,
    // State setters for complex interactions
    setDialogOpen,
    // expose stored wait list event
    waitListEvent,
    // Deletion dialog controls
    deletionDialogOpen,
    deletionMessage,
    confirmDeletion,
    closeDeletionDialog,
    // Edit-title dialog controls
    editTitleDialogOpen,
    editTitleEvent,
    confirmEditTitle,
    closeEditTitleDialog
  };
};
